import React from 'react';
import { X, Zap, Phone, ExternalLink } from 'lucide-react';
import { designTokens } from '../styles/design-tokens';
import { useTheme } from '../context/ThemeContext';
import { Section, Field } from './education/Section';

export interface ElectricTerritoryProperties {
  id: number;
  company_name: string;
  company_abbreviation: string | null;
  company_type: string;
  iso_rto: string | null;
  main_phone: string | null;
  outage_phone: string | null;
  outage_map_url: string | null;
  website: string | null;
  gt_cooperative: string | null;
}

interface Props {
  territory: ElectricTerritoryProperties;
  onClose: () => void;
}

const TYPE_INFO: Record<string, { label: string; desc: string }> = {
  IOU: {
    label: 'Investor-Owned Utility',
    desc: 'Regulated by PUCT. Largest service areas. Interconnection requests go through their planning process. BTM projects in IOU territory may face more scrutiny for co-location (SB 6).',
  },
  COOP: {
    label: 'Electric Cooperative',
    desc: 'Member-owned, governed by elected board. Often more receptive to large industrial loads. Smaller territories, but can be easier to negotiate power purchase agreements with for hybrid BTM/grid projects.',
  },
  MUNI: {
    label: 'Municipal Utility',
    desc: 'City-owned and operated. Subject to local government approval. May have economic development incentives for data centers. Smaller coverage, concentrated in city limits.',
  },
};

const ISO_INFO: Record<string, { label: string; color: string; desc: string }> = {
  ERCOT: {
    label: 'ERCOT',
    color: '#0ea5e9',
    desc: 'Texas grid — isolated from Eastern/Western interconnections. SB 6 applies: 75 MW+ co-located loads need PUCT approval. Fully off-grid BTM is exempt. ERCOT queue has 233 GW of pending large-load requests (5-7 yr wait), making BTM the fast path.',
  },
  SPP: {
    label: 'Southwest Power Pool',
    color: '#f59e0b',
    desc: 'Covers TX panhandle and parts of West TX. FERC-regulated. Different interconnection rules than ERCOT — subject to federal wholesale market rules. SB 6 does NOT apply.',
  },
  MISO: {
    label: 'Midcontinent ISO',
    color: '#10b981',
    desc: 'Covers parts of East TX (Entergy territory). FERC-regulated. Separate interconnection queue from ERCOT. SB 6 does NOT apply.',
  },
  WECC: {
    label: 'Western Interconnection',
    color: '#dc2626',
    desc: 'Covers El Paso area (El Paso Electric). FERC-regulated. Connected to Western US grid. SB 6 does NOT apply. El Paso is in ozone nonattainment — air permitting is harder here.',
  },
};

export const ElectricTerritoryDetail: React.FC<Props> = ({ territory, onClose }) => {
  const { theme } = useTheme();
  const t = territory;
  const typeInfo = TYPE_INFO[t.company_type];
  const isoInfo = t.iso_rto ? ISO_INFO[t.iso_rto] : null;
  const accent = isoInfo?.color || '#6b7280';

  return (
    <div style={{
      width: designTokens.layout.detailPanelWidth, background: theme.background,
      borderLeft: `1px solid ${theme.border}`,
      display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0,
    }}>
      {/* Header */}
      <div style={{
        padding: '16px', borderBottom: `1px solid ${theme.border}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <Zap size={18} color={accent} />
            <h2 style={{ fontSize: '15px', fontWeight: 700, margin: 0, color: theme.text }}>
              {t.company_abbreviation || t.company_name}
            </h2>
          </div>
          {t.company_abbreviation && (
            <div style={{ fontSize: '12px', color: theme.textSecondary, marginLeft: '26px' }}>
              {t.company_name}
            </div>
          )}
        </div>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
          color: theme.textSecondary,
        }}>
          <X size={18} />
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
        {/* ISO/RTO badge */}
        {isoInfo && (
          <div style={{
            padding: '10px 14px', marginBottom: '16px', borderRadius: '8px',
            background: accent + '15', border: `1px solid ${accent}30`,
          }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: accent, marginBottom: '4px' }}>
              {isoInfo.label}
            </div>
            <div style={{ fontSize: '11px', color: theme.text, lineHeight: 1.6 }}>
              {isoInfo.desc}
            </div>
          </div>
        )}

        {/* Utility type */}
        <Section title="Utility Type">
          <Field label="Type" value={typeInfo?.label || t.company_type} />
          {t.gt_cooperative && <Field label="G&T Cooperative" value={t.gt_cooperative} />}
          {typeInfo && (
            <div style={{ fontSize: '11px', color: theme.textSecondary, lineHeight: 1.6, marginTop: '8px' }}>
              {typeInfo.desc}
            </div>
          )}
        </Section>

        {/* Grid context */}
        <Section title="Grid Context">
          <Field label="ISO/RTO" value={t.iso_rto || 'Not assigned'} />
          <Field label="SB 6 Applies" value={t.iso_rto === 'ERCOT' ? 'Yes (75 MW+ co-located)' : 'No'} />
          <Field label="BTM Exempt" value={t.iso_rto === 'ERCOT' ? 'Yes (fully off-grid)' : 'N/A'} />
        </Section>

        {/* Contact info */}
        <Section title="Contact">
          {t.main_phone && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 0', borderBottom: `1px solid ${theme.borderLight}` }}>
              <Phone size={12} color={theme.textTertiary} />
              <span style={{ fontSize: '12px', color: theme.textTertiary }}>Main</span>
              <a href={`tel:${t.main_phone.replace(/[^\d+]/g, '')}`} style={{ fontSize: '13px', color: designTokens.colors.brand, marginLeft: 'auto', fontFamily: designTokens.typography.fontFamily.mono }}>
                {t.main_phone}
              </a>
            </div>
          )}
          {t.outage_phone && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 0', borderBottom: `1px solid ${theme.borderLight}` }}>
              <Phone size={12} color={theme.textTertiary} />
              <span style={{ fontSize: '12px', color: theme.textTertiary }}>Outage</span>
              <a href={`tel:${t.outage_phone.replace(/[^\d+]/g, '')}`} style={{ fontSize: '13px', color: designTokens.colors.brand, marginLeft: 'auto', fontFamily: designTokens.typography.fontFamily.mono }}>
                {t.outage_phone}
              </a>
            </div>
          )}
        </Section>

        {/* Links */}
        {(t.website || t.outage_map_url) && (
          <Section title="Links">
            {t.website && (
              <a href={t.website} target="_blank" rel="noopener noreferrer"
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 0', fontSize: '12px', color: designTokens.colors.brand, textDecoration: 'none' }}>
                <ExternalLink size={12} /> Company Website
              </a>
            )}
            {t.outage_map_url && (
              <a href={t.outage_map_url} target="_blank" rel="noopener noreferrer"
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 0', fontSize: '12px', color: designTokens.colors.brand, textDecoration: 'none' }}>
                <ExternalLink size={12} /> Live Outage Map
              </a>
            )}
          </Section>
        )}

        {/* BTM relevance */}
        <Section title="BTM Site Selection Notes">
          <div style={{ fontSize: '11px', color: theme.textSecondary, lineHeight: 1.7 }}>
            {t.iso_rto === 'ERCOT' && (
              <>
                <div style={{ marginBottom: '8px' }}>
                  <strong style={{ color: theme.text }}>ERCOT territory — primary BTM market.</strong> The 233 GW interconnection queue (5-7 yr wait) makes behind-the-meter the fastest path to power for data centers.
                </div>
                <div style={{ marginBottom: '8px' }}>
                  SB 6 requires PUCT approval for 75 MW+ co-located loads. <strong>Fully off-grid BTM is exempt.</strong> This is the regulatory advantage of BTM in ERCOT.
                </div>
              </>
            )}
            {t.iso_rto === 'SPP' && (
              <div style={{ marginBottom: '8px' }}>
                SPP territory in TX panhandle. FERC-regulated, separate interconnection queue from ERCOT. BTM still viable here — same gas pipeline infrastructure, different regulatory framework.
              </div>
            )}
            {t.iso_rto === 'WECC' && (
              <div style={{ marginBottom: '8px' }}>
                El Paso Electric territory. Ozone nonattainment area — TCEQ air permitting is harder here. Meta&apos;s 1 GW project faces community opposition. Not ideal for BTM without strong gas infrastructure.
              </div>
            )}
            {t.company_type === 'COOP' && (
              <div style={{ marginBottom: '8px' }}>
                Electric cooperatives are often more amenable to large industrial customers than IOUs. Board-governed — a direct relationship with the local co-op can accelerate approvals for hybrid (BTM + grid export) arrangements.
              </div>
            )}
            {t.company_type === 'MUNI' && (
              <div style={{ marginBottom: '8px' }}>
                Municipal utilities may offer economic development rates or tax incentives for data center projects. Check city council posture on data centers — some TX municipalities are welcoming, others have pushed moratoriums.
              </div>
            )}
            <div style={{ color: theme.textTertiary, fontStyle: 'italic' }}>
              Note: BTM projects generate their own power from pipeline gas. The utility territory matters primarily for backup grid connection, excess power export, and regulatory context.
            </div>
          </div>
        </Section>

        {/* Source */}
        <Section title="Data Source">
          <div style={{ fontSize: '11px', color: theme.textTertiary, lineHeight: 1.6 }}>
            <div>Texas Public Utility Commission</div>
            <div>Electric Service Territory Boundaries</div>
            <div style={{ marginTop: '4px' }}>
              <a href="https://www.puc.texas.gov/storm/map/" target="_blank" rel="noopener noreferrer"
                style={{ color: designTokens.colors.brand, textDecoration: 'underline', fontSize: '10px' }}>
                PUCT Storm Map
              </a>
            </div>
          </div>
        </Section>
      </div>
    </div>
  );
};
